// ── Attendance state model (v2 Softening) ──
//
// Canonical source: docs/design/home-and-attendance.md §1.
// Every `PlayerEntity` is in exactly one of five attendance states at all
// times. Transitions are server-authoritative and fully enumerated in §1.2.
//
// This module is pure: no Phaser, no Colyseus, no runtime side-effects. All
// functions here are deterministic and mutation-free, suitable for use on
// either client or server (and inside unit tests).
/**
 * The five attendance states a player character may occupy.
 *
 * - `Active`         — logged in and moving/acting. Normal play. No accrual.
 * - `ParkedHearth`   — logged in, lying at a home or shared village hearth.
 *                      HP/stamina regen, hearth-bond ticks with co-parked
 *                      players, small passive material trickle.
 * - `ParkedShrine`   — logged in, lying at a realm shrine. No HP regen;
 *                      realm-mastery + shrine ritual counters accrue.
 * - `Ghost`          — logged out while NOT parked. Max-HP decays ~1% per
 *                      real hour, floored at 20%. Semi-transparent figure
 *                      visible in-world at logout position.
 * - `Dreaming`       — logged out while parked at hearth or shrine. No
 *                      decay; whisper accrual, pet-bonding / realm-mastery
 *                      credit based on park location.
 *
 * String-valued for stable serialization across client/server/persistence.
 */
export var AttendanceState;
(function (AttendanceState) {
    AttendanceState["Active"] = "active";
    AttendanceState["ParkedHearth"] = "parked-hearth";
    AttendanceState["ParkedShrine"] = "parked-shrine";
    AttendanceState["Ghost"] = "ghost";
    AttendanceState["Dreaming"] = "dreaming";
})(AttendanceState || (AttendanceState = {}));
/**
 * In-world hours until ghost-return weakness clears, expressed in ms.
 * Re-exported from `accrual.ts` to keep `transition()` self-contained; the
 * canonical constant lives alongside the other accrual tunables.
 *
 * Duplicated here as a local literal to avoid a cross-file import cycle at
 * module init; value must match `accrual.ts#RETURN_WEAKNESS_DURATION_MS`.
 *
 * 24 in-world hours × 60 minutes × 1000 ms/min  →  a 24-minute real-time
 * recovery window (the in-world:real-world ratio chosen in `accrual.ts`).
 */
const RETURN_WEAKNESS_DURATION_MS_LOCAL = 24 * 60 * 1000;
/**
 * Pure, deterministic attendance-state transition function.
 *
 * Given the previous `AttendanceRecord`, an `AttendanceEvent`, and the
 * current wall-clock time (`now`, ms since epoch), returns a new
 * `AttendanceRecord` with the transition applied. The input record is
 * never mutated.
 *
 * Rules implemented from `docs/design/home-and-attendance.md` §1.2 and
 * §1.7:
 *
 * - `login` while `Ghost` → Active with `returningWeakenedUntil` set to
 *   `now + RETURN_WEAKNESS_DURATION_MS`.
 * - `login` while `Dreaming` → Active, no debuff.
 * - `login` while `Active` (idempotent, e.g. reconnect) → Active, cleared
 *   park fields.
 * - `logout` while `Active` → Ghost, stamp `parkedAt` with caller-known
 *   position only if `prev.parkedAt` exists; otherwise leave null and let
 *   caller fill in. (Position is passed through the record, not the event.)
 * - `logout` while `ParkedHearth` or `ParkedShrine` → Dreaming.
 * - `logout` while already offline (Ghost/Dreaming) → no change.
 * - `lie-hearth` from Active → ParkedHearth.
 * - `lie-shrine` from Active → ParkedShrine.
 * - `stand` or `move` from any parked state → Active, park fields cleared.
 * - `hearth-structure-destroyed` while ParkedHearth → Active at
 *   `parkedAt`, park fields cleared. No HP/weakness penalty.
 * - `expedition-enter` / `expedition-exit` — state preserved; only
 *   `lastTransitionAt` is advanced. Scoped-clock handoff happens outside
 *   this function (§1.7 Expedition hearths).
 *
 * Any combination not listed above is treated as a no-op that still
 * advances `lastTransitionAt` (so audits see the event).
 */
export function transition(prev, event, now) {
    // Shallow-copy to enforce purity; we only ever return new objects.
    const next = {
        state: prev.state,
        parkedStructureId: prev.parkedStructureId,
        parkedAt: prev.parkedAt ? { x: prev.parkedAt.x, y: prev.parkedAt.y } : null,
        parkedSince: prev.parkedSince,
        lastTransitionAt: now,
        returningWeakenedUntil: prev.returningWeakenedUntil,
        maxHpMultiplier: prev.maxHpMultiplier,
    };
    switch (event.kind) {
        case 'lie-hearth': {
            if (prev.state === AttendanceState.Active) {
                next.state = AttendanceState.ParkedHearth;
                next.parkedSince = now;
                // Reaching a hearth clears return-weakness immediately (§1.4).
                next.returningWeakenedUntil = 0;
            }
            return next;
        }
        case 'lie-shrine': {
            if (prev.state === AttendanceState.Active) {
                next.state = AttendanceState.ParkedShrine;
                next.parkedSince = now;
            }
            return next;
        }
        case 'stand':
        case 'move': {
            if (prev.state === AttendanceState.ParkedHearth ||
                prev.state === AttendanceState.ParkedShrine) {
                next.state = AttendanceState.Active;
                next.parkedStructureId = null;
                next.parkedAt = null;
                next.parkedSince = now;
            }
            return next;
        }
        case 'logout': {
            if (prev.state === AttendanceState.Active) {
                next.state = AttendanceState.Ghost;
                next.parkedSince = now;
            }
            else if (prev.state === AttendanceState.ParkedHearth ||
                prev.state === AttendanceState.ParkedShrine) {
                next.state = AttendanceState.Dreaming;
                next.parkedSince = now;
            }
            // Ghost/Dreaming → no change.
            return next;
        }
        case 'login': {
            if (prev.state === AttendanceState.Ghost) {
                next.state = AttendanceState.Active;
                next.returningWeakenedUntil = now + RETURN_WEAKNESS_DURATION_MS_LOCAL;
                next.parkedStructureId = null;
                next.parkedAt = null;
                next.parkedSince = now;
            }
            else if (prev.state === AttendanceState.Dreaming) {
                next.state = AttendanceState.Active;
                next.parkedStructureId = null;
                next.parkedAt = null;
                next.parkedSince = now;
            }
            else if (prev.state === AttendanceState.Active) {
                // Idempotent reconnect: refresh timers but preserve state.
                next.parkedSince = now;
            }
            // Parked* on login is unusual (server should have transitioned to
            // Dreaming on the prior logout); treat as a no-op beyond timestamp.
            return next;
        }
        case 'hearth-structure-destroyed': {
            if (prev.state === AttendanceState.ParkedHearth ||
                prev.state === AttendanceState.Dreaming) {
                // §1.7: auto-transition to Active at the hearth's position.
                next.state = AttendanceState.Active;
                next.parkedStructureId = null;
                // Keep `parkedAt` so the caller can reposition the avatar there;
                // clear it only after repositioning. We clear here to keep the
                // record canonical for an Active character.
                next.parkedAt = null;
                next.parkedSince = now;
            }
            return next;
        }
        case 'expedition-enter':
        case 'expedition-exit': {
            // State is preserved; the scoped-clock handoff happens outside.
            return next;
        }
        default: {
            // Exhaustiveness guard. Any future event lands here; return a copy so
            // callers still observe an advanced `lastTransitionAt`.
            const _exhaustive = event;
            void _exhaustive;
            return next;
        }
    }
}
//# sourceMappingURL=state.js.map